import { useState } from "react";
import { MapPin, AlertTriangle, Wrench, Leaf, Building2, TrendingDown, TrendingUp, Printer, Share2, Info, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useLang } from "@/lib/lang-context";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { trackEvent } from "@/lib/analytics";
import StatusTag from "./StatusTag";
import AcquisitionCost from "./AcquisitionCost";
import RentalAnalysis from "./RentalAnalysis";
import CondoFees from "./CondoFees";
import ImuEstimate from "./ImuEstimate";
import MortgageCashflow from "./MortgageCashflow";
import ScenarioSnapshots from "./ScenarioSnapshots";

// Industrial defaults — mirror MortgageCashflow.jsx (Italian mortgage averages, 2026).
const DEFAULT_MORTGAGE_RATE = 3.5;
const DEFAULT_MORTGAGE_DOWN = 20;
const DEFAULT_MORTGAGE_YEARS = 25;

const fmtPrice = (n) =>
  n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const fmtNum = (n, suffix = "") =>
  n == null ? "—" : `${new Intl.NumberFormat("it-IT", { maximumFractionDigits: 2 }).format(n)}${suffix}`;

const fmtPct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`);

export const EvaluationSheet = ({ a, onOpenPaywall }) => {
  const { t } = useLang();
  // Lift condo-fee inclusion state up here so both `CondoFees` (display
  // toggle) and `MortgageCashflow` (cashflow recompute) stay in sync.
  // Default true to mirror the previous behaviour where the estimate
  // always counted toward the cashflow. Declared BEFORE the early-return
  // below so React's hook order rule is respected.
  const [condoIncluded, setCondoIncluded] = useState(true);
  // Mortgage parameters lifted up here so `ScenarioSnapshots` can both
  // read them when saving a snapshot AND drive them back when the user
  // restores one. `MortgageCashflow` becomes a controlled child.
  const [mortgageRate, setMortgageRate] = useState(DEFAULT_MORTGAGE_RATE);
  const [mortgageDown, setMortgageDown] = useState(DEFAULT_MORTGAGE_DOWN);
  const [mortgageYears, setMortgageYears] = useState(DEFAULT_MORTGAGE_YEARS);
  if (!a) return null;

  const surfaceLabel =
    a.surface_value && a.surface_unit
      ? `${fmtNum(a.surface_value)} ${a.surface_unit}${a.surface_sqm && a.surface_unit !== "sqm" ? ` (${fmtNum(a.surface_sqm)} m²)` : ""}`
      : "—";

  const diffPositive = a.omi_difference_pct != null && a.omi_difference_pct > 0;
  const DiffIcon = diffPositive ? TrendingUp : TrendingDown;

  // Annual saving if the user flips the toggle to "Non previste".
  // We use the mid-point of the backend min/max range — that's the same
  // figure visualised in the CondoFees card.
  const condoAnnualSaving =
    !condoIncluded && a.condo_fee_estimate
      ? Math.round(
          ((a.condo_fee_estimate.annual_min_eur ?? 0) +
            (a.condo_fee_estimate.annual_max_eur ?? 0)) /
            2
        )
      : 0;

  // Share URL uses the `/api/share/:id` backend route so social crawlers get
  // OG tags (FB/WhatsApp/Twitter all fetch with a static client). Humans
  // hit the HTML's `<meta http-equiv="refresh">` and land at `/sheet/:id`.
  const shareUrl = a.id
    ? `${window.location.origin}/api/share/${a.id}`
    : null;

  const onShare = async () => {
    if (!shareUrl) return;
    // Prefer the native share sheet on mobile; fall back to clipboard.
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${a.total_score}/10 · ${a.status} · ${a.town || "Salento"}`,
          text: a.title || t("sheet.title"),
          url: shareUrl,
        });
        trackEvent("report_shared", {
          method: "native",
          analysis_id: a.id,
          score: a.total_score,
          status: a.status,
          town: a.town,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      trackEvent("report_shared", {
        method: "clipboard",
        analysis_id: a.id,
        score: a.total_score,
        status: a.status,
        town: a.town,
      });
      toast.success(t("share.copied"));
    } catch (err) {
      // User dismissed the native share sheet — not an error worth surfacing.
      if (err?.name !== "AbortError") {
        toast.error(t("share.failed"));
      }
    }
  };

  return (
    <article
      data-testid="evaluation-sheet"
      data-print-sheet="true"
      className="bg-white border-2 border-[#282C2A] shadow-paper rounded-sm fade-up print-sheet"
    >
      {/* Print + Share buttons */}
      <div className="px-6 md:px-10 pt-6 flex justify-end gap-3 print:hidden">
        {shareUrl && (
          <button
            type="button"
            data-testid="share-btn"
            onClick={onShare}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-[#4A5D23] bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
          >
            <Share2 size={13} strokeWidth={1.5} />
            {t("sheet.share")}
          </button>
        )}
        <button
          type="button"
          data-testid="print-pdf-btn"
          onClick={() => {
            // Free-tier reports cannot be downloaded — surface the paywall
            // copy instead of silently triggering the print dialog.
            if (a.paid_report === false) {
              trackEvent("paywall_shown", {
                reason: "pdf_locked",
                analysis_id: a.id,
              });
              if (typeof onOpenPaywall === "function") onOpenPaywall();
              return;
            }
            trackEvent("pdf_downloaded", {
              analysis_id: a.id,
              score: a.total_score,
              status: a.status,
              town: a.town,
            });
            window.print();
          }}
          aria-disabled={a.paid_report === false}
          title={a.paid_report === false ? t("sheet.printPdfLocked") : undefined}
          className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
            a.paid_report === false
              ? "border border-[#C9C6BB] text-[#A7A39B] cursor-not-allowed bg-[#F2EFE6]"
              : "border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white"
          }`}
        >
          {a.paid_report === false ? (
            <Lock size={13} strokeWidth={1.5} />
          ) : (
            <Printer size={13} strokeWidth={1.5} />
          )}
          {t("sheet.printPdf")}
        </button>
      </div>
      {/* Free-preview banner — explains why print is locked. */}
      {a.paid_report === false && (
        <div
          data-testid="free-preview-banner"
          className="mx-6 md:mx-10 mt-3 border border-[#E0BBA0] bg-[#FFF1E6] px-3 py-2 text-[11px] text-[#7A3A12] flex items-center gap-2 print:hidden"
        >
          <Lock size={11} strokeWidth={1.75} />
          <span>{t("sheet.freePreview")}</span>
          {typeof onOpenPaywall === "function" && (
            <button
              type="button"
              onClick={onOpenPaywall}
              data-testid="free-preview-cta"
              className="ml-auto font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#7A3A12] hover:bg-[#7A3A12] hover:text-white transition-colors"
            >
              {t("sheet.unlockDownload")}
            </button>
          )}
        </div>
      )}
      {/* Header */}
      <div className="px-6 md:px-10 pt-4 pb-6 border-b border-[#E6E4DD]">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <MapPin size={12} strokeWidth={1.5} />
              {a.town || t("sheet.unknown")}
              {a.province && <span className="text-[#6C706D]"> · {a.province}</span>}
              {a.area_description && <span className="text-[#6C706D]"> · {a.area_description}</span>}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-3 text-[#282C2A] leading-none">
              {t("sheet.title")}
            </h2>
            {a.title && (
              <p className="mt-3 text-sm text-[#6C706D] max-w-2xl line-clamp-2">
                {a.title}
              </p>
            )}
          </div>
          <StatusTag status={a.status} size="lg" />
        </div>
      </div>

      {/* Score */}
      <div className="grid grid-cols-1 md:grid-cols-3 border-b border-[#E6E4DD]">
        <div className="md:col-span-1 px-6 md:px-10 py-8 md:border-r border-[#E6E4DD] flex flex-col items-start justify-center">
          <div className="label-eyebrow">{t("sheet.score")}</div>
          <div className="mt-1 flex items-end gap-2">
            <span data-testid="total-score" className="font-serif-display text-7xl md:text-8xl leading-none text-[#282C2A]">
              {a.total_score}
            </span>
            <span className="font-mono text-lg text-[#6C706D] pb-3">/ 10</span>
          </div>
        </div>
        <div className="md:col-span-2 px-6 md:px-10 py-8">
          <div className="label-eyebrow">{t("sheet.breakdown")}</div>
          <TooltipProvider delayDuration={150}>
            <ul className="mt-3 space-y-2 text-sm">
              <BreakdownRow
                label={t("sheet.omiBonus")}
                value={a.score_breakdown?.omi_bonus ?? 0}
                ruleKey="sheet.rules.omi"
                testId="score-omi-bonus"
              />
              <BreakdownRow
                label={t("sheet.buildingBonus")}
                value={a.score_breakdown?.building_bonus ?? 0}
                ruleKey="sheet.rules.building"
                testId="score-building-bonus"
              />
              <BreakdownRow
                label={t("sheet.resourceBonus")}
                value={a.score_breakdown?.resource_bonus ?? 0}
                ruleKey="sheet.rules.resource"
                testId="score-resource-bonus"
              />
              <BreakdownRow
                label={t("sheet.penalty")}
                value={a.score_breakdown?.restriction_penalty ?? 0}
                ruleKey="sheet.rules.penalty"
                testId="score-penalty"
                isPenalty
              />
            </ul>
          </TooltipProvider>
        </div>
      </div>

      {/* Economic */}
      <div className="px-6 md:px-10 pt-8 pb-2">
        <div className="label-eyebrow">{t("sheet.economic")}</div>
      </div>
      <div className="appraisal-grid grid grid-cols-2">
        <div>
          <div className="label-eyebrow">{t("sheet.omiLocal")}</div>
          <div className="font-mono text-xl mt-2" data-testid="omi-ref">
            {a.omi_reference_eur_sqm != null ? `€ ${fmtNum(a.omi_reference_eur_sqm)} / m²` : "—"}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("sheet.ppsqm")}</div>
          <div className="font-mono text-xl mt-2" data-testid="price-per-sqm">
            {a.price_per_sqm != null ? `€ ${fmtNum(a.price_per_sqm)} / m²` : "—"}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("sheet.asking")}</div>
          <div className="font-mono text-xl mt-2">{fmtPrice(a.asking_price)}</div>
        </div>
        <div>
          <div className="label-eyebrow">{t("sheet.diff")}</div>
          <div
            data-testid="omi-diff"
            className={`font-mono text-xl mt-2 inline-flex items-center gap-2 ${
              a.omi_difference_pct == null
                ? ""
                : a.omi_difference_pct < 0
                ? "text-[#4A5D23]"
                : "text-[#A0522D]"
            }`}
          >
            {a.omi_difference_pct != null && <DiffIcon size={18} strokeWidth={1.5} />}
            {fmtPct(a.omi_difference_pct)}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("sheet.surface")}</div>
          <div className="font-mono text-xl mt-2">{surfaceLabel}</div>
        </div>
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Building2 size={12} strokeWidth={1.5} />
            {t("sheet.potential")}
          </div>
          <div className="font-mono text-xl mt-2">{a.potential_use || "—"}</div>
        </div>
      </div>

      {/* Technical note */}
      {a.technical_note && (
        <div className="px-6 md:px-10 py-8 border-t border-[#E6E4DD]">
          <div className="label-eyebrow flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.5} />
            {t("sheet.tech")}
          </div>
          <p className="mt-3 font-serif-display text-xl md:text-2xl leading-snug text-[#282C2A] max-w-3xl">
            “{a.technical_note}”
          </p>
        </div>
      )}

      {/* Acquisition cost calculator — Italian taxes + notary + agency */}
      <AcquisitionCost
        price={a.asking_price}
        isAgricultural={
          a.potential_use === "Agricultural" ||
          a.potential_use === "Building land" ||
          /agricol|terreno/i.test(a.potential_use || "")
        }
      />

      {/* Estimated condo fees — apartments only (hidden otherwise). */}
      <CondoFees
        estimate={a.condo_fee_estimate}
        included={condoIncluded}
        onToggle={setCondoIncluded}
      />

      {/* Annual IMU estimate — residential/commercial only (hidden for terreni). */}
      <ImuEstimate estimate={a.imu_estimate} />

      {/* Rental investment scenarios — hidden when not applicable (see core/rental.py) */}
      <RentalAnalysis
        estimate={a.rental_estimate}
        bonus={a.score_breakdown?.rental_bonus ?? 0}
        netYield={a.net_yield_estimate}
        condoIncluded={condoIncluded}
      />

      {/* Mortgage cashflow — locked controls for free users, editable for premium. */}
      <MortgageCashflow
        price={a.asking_price}
        rental={a.rental_estimate}
        paidReport={a.paid_report}
        onOpenPaywall={onOpenPaywall}
        annualSaving={condoAnnualSaving}
        rate={mortgageRate}
        setRate={setMortgageRate}
        down={mortgageDown}
        setDown={setMortgageDown}
        years={mortgageYears}
        setYears={setMortgageYears}
      />

      {/* Scenario snapshots — save & compare "what-if" simulations locally.
          Anchored after the mortgage section so the user has touched every
          knob (condo toggle, mortgage sliders) before being invited to save. */}
      <ScenarioSnapshots
        analysisId={a.id}
        condoIncluded={condoIncluded}
        setCondoIncluded={setCondoIncluded}
        rate={mortgageRate}
        setRate={setMortgageRate}
        down={mortgageDown}
        setDown={setMortgageDown}
        years={mortgageYears}
        setYears={setMortgageYears}
        price={a.asking_price}
        rental={a.rental_estimate}
        netYield={a.net_yield_estimate}
        hasMortgage={Number(a.asking_price) > 0 && Number(a.rental_estimate?.longterm?.annual_net_eur) > 0}
      />

      {/* Bonuses */}
      {(a.building_terms?.length > 0 || a.resource_terms?.length > 0) && (
        <div className="px-6 md:px-10 py-6 border-t border-[#E6E4DD] grid grid-cols-1 md:grid-cols-2 gap-6">
          {a.building_terms?.length > 0 && (
            <div>
              <div className="label-eyebrow flex items-center gap-2">
                <Building2 size={12} strokeWidth={1.5} />
                {t("sheet.buildingBonus")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {a.building_terms.map((tg) => (
                  <span key={tg} className="px-3 py-1 text-xs uppercase tracking-wider bg-[#4A5D23] text-white">
                    {tg}
                  </span>
                ))}
              </div>
            </div>
          )}
          {a.resource_terms?.length > 0 && (
            <div>
              <div className="label-eyebrow flex items-center gap-2">
                <Leaf size={12} strokeWidth={1.5} />
                {t("sheet.resourceBonus")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {a.resource_terms.map((tg) => (
                  <span key={tg} className="px-3 py-1 text-xs uppercase tracking-wider bg-[#F0EFEB] border border-[#E6E4DD] text-[#282C2A]">
                    {tg}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Risks */}
      <div className="px-6 md:px-10 py-6 border-t border-[#E6E4DD]">
        <div className="label-eyebrow flex items-center gap-2">
          <AlertTriangle size={12} strokeWidth={1.5} />
          {t("sheet.risks")}
        </div>
        {a.risks?.length > 0 ? (
          <ul className="mt-3 space-y-3">
            {a.risks.map((r, i) => (
              <li
                key={i}
                data-testid={`risk-item-${i}`}
                className="border-l-2 border-[#A0522D] pl-3 py-1"
              >
                <div className="flex items-start gap-3">
                  <span
                    data-testid={`risk-severity-${i}`}
                    className="font-mono text-xs uppercase tracking-wider px-2 py-0.5 bg-[#FBEBE5] text-[#A0522D] border border-[#F5C6B5]"
                  >
                    {r.severity}
                  </span>
                  <span className="text-sm font-medium text-[#282C2A]">{r.label}</span>
                </div>
                {r.detail && (
                  <p
                    data-testid={`risk-detail-${i}`}
                    className="mt-2 text-xs leading-relaxed text-[#3F4543]"
                  >
                    {r.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-[#6C706D]">{t("sheet.noRisks")}</p>
        )}
      </div>

      {/* Cynical warning */}
      {a.cynical_warning && (
        <div className="mx-6 md:mx-10 mb-8 mt-2 p-5 bg-[#FBEBE5] border-l-4 border-[#A0522D]" data-testid="cynical-warning">
          <div className="label-eyebrow text-[#A0522D]">{t("sheet.cynical")}</div>
          <p className="mt-2 font-serif-display text-lg text-[#282C2A]">{a.cynical_warning}</p>
        </div>
      )}

      {/* Footer meta */}
      <div className="px-6 md:px-10 py-4 border-t border-[#E6E4DD] flex items-center justify-between text-xs font-mono text-[#6C706D] flex-wrap gap-2">
        <span>
          {t("sheet.savedAt")}: {new Date(a.created_at).toLocaleString()}
        </span>
        {a.source_url && (
          <a
            href={a.source_url}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-[#4A5D23]"
            data-testid="source-link"
          >
            {t("sheet.source")} ↗
          </a>
        )}
      </div>

      {/* Legal disclaimer — printed at the bottom of every report (free or
          paid). Must stay visible in print so the PDF carries the same legal
          notice as the live page. */}
      <div
        data-testid="sheet-disclaimer"
        className="px-6 md:px-10 pb-6 pt-2 text-[10px] leading-relaxed text-[#6C706D] italic border-t border-[#E6E4DD]"
      >
        <span className="font-mono uppercase tracking-widest not-italic text-[#282C2A] mr-2">
          {t("sheet.disclaimerLabel")}
        </span>
        {t("sheet.disclaimer")}
      </div>

      {/* Powered-by banner — visible both on screen and in print/PDF. Doubles
          as free distribution every time the sheet is shared. QR code lets
          someone scanning a printed copy land directly on dealrating.it. */}
      <div
        data-testid="powered-by-banner"
        className="px-6 md:px-10 py-4 border-t border-[#E6E4DD] bg-[#F0EFEB] flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <div className="h-7 w-7 border border-[#282C2A] flex items-center justify-center shrink-0">
            <span className="font-serif-display text-[#282C2A] text-base leading-none">D</span>
          </div>
          <div className="leading-tight">
            <div className="label-eyebrow text-[10px] text-[#6C706D]">Powered by</div>
            <a
              href="https://dealrating.it/?utm_source=sheet&utm_medium=text-link"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="powered-by-link"
              className="font-serif-display text-base text-[#282C2A] hover:text-[#4A5D23] transition-colors no-underline"
            >
              Deal Rating<span className="text-[#4A5D23]">.it</span>
            </a>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="label-eyebrow text-[9px] text-[#6C706D]">Scansiona</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#282C2A]">
              dealrating.it
            </div>
          </div>
          <div
            data-testid="powered-by-qr"
            className="bg-white p-1.5 border border-[#E6E4DD] shrink-0"
            aria-label="QR code to dealrating.it"
          >
            <QRCodeSVG
              value="https://dealrating.it/?utm_source=sheet&utm_medium=qr"
              size={56}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#282C2A"
              marginSize={0}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

/**
 * One row of the score breakdown with a hover/focus tooltip explaining
 * how the rule is applied. Bridges the credibility gap of a black-box
 * AI score by letting the user reverse-engineer each component.
 *
 * - `value` is the integer bonus/penalty.
 * - `ruleKey` resolves to a short rule sentence via i18n (one per rule).
 * - `isPenalty=true` switches the colour palette to terracotta + drops
 *   the leading `+` (so we render `-5` not `+-5`).
 *
 * The tooltip trigger is the small Info icon — gives a clear affordance
 * without polluting the label text. Keyboard-focusable for a11y.
 */
const BreakdownRow = ({ label, value, ruleKey, testId, isPenalty }) => {
  const { t } = useLang();
  const positive = !isPenalty && value > 0;
  const valueClass = isPenalty
    ? "text-[#A0522D]"
    : positive
    ? "text-[#4A5D23]"
    : "text-[#282C2A]";
  return (
    <li
      data-testid={testId}
      className="flex items-center justify-between gap-3 border-b border-dashed border-[#E6E4DD] py-1.5 last:border-b-0"
    >
      <span className="inline-flex items-center gap-1.5 text-[#6C706D]">
        {label}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`${label} — rule`}
              data-testid={`${testId}-info`}
              className="inline-flex h-4 w-4 items-center justify-center text-[#A6AAA7] hover:text-[#282C2A] focus:outline-none focus:text-[#4A5D23] transition-colors"
            >
              <Info size={12} strokeWidth={1.5} />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className="max-w-[280px] bg-[#282C2A] text-white border-0 px-3 py-2 text-[11px] leading-snug font-normal normal-case tracking-normal"
          >
            {t(ruleKey)}
          </TooltipContent>
        </Tooltip>
      </span>
      <span className={`font-mono ${valueClass}`}>
        {isPenalty ? value : `+${value}`}
      </span>
    </li>
  );
};

export default EvaluationSheet;
